import { useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Phone, MessageSquare, ClipboardList, Truck, Route, Wrench, BatteryCharging,
  KeyRound, Disc3, Fuel, Anchor, CarFront, Construction, Building2, MapPin,
  Instagram, Send, ImagePlus, Clock, ShieldCheck, User, Star, X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "(561) 769-9615";
const PHONE_TEL = "tel:+15617699615";
const PHONE_SMS = "sms:+15617699615";

const ASSETS = "https://customer-assets-7cd3h4nn.emergentagent.net/job_fc2cbf62-5317-4b8d-80ee-b5f65a80cd80/artifacts";
const LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_broward-towing/artifacts/n0kxfmnc_IMG-20260930-WA0002.jpg";
const IMG_GARAGE = `${ASSETS}/9guz4eod_IMG-20260930-WA0001.jpg`;
const IMG_LOT = `${ASSETS}/kpu98haz_IMG-20260930-WA0006.webp`;
const IMG_STREET = `${ASSETS}/kvoj83l1_IMG-20260930-WA0005.jpg`;

const SERVICES = [
  { name: "Local Towing", icon: Truck, desc: "Fast local towing across Greater Fort Lauderdale and Broward County." },
  { name: "Long-Distance Towing", icon: Route, desc: "Moving a vehicle across South Florida or beyond? We haul it." },
  { name: "Roadside Assistance", icon: Wrench, desc: "Stranded on the shoulder? Real help, on the way." },
  { name: "Jump Starts", icon: BatteryCharging, desc: "Dead battery? We get you started and back on the road." },
  { name: "Lockouts", icon: KeyRound, desc: "Locked out of your vehicle? Careful, damage-free entry." },
  { name: "Tire Changes", icon: Disc3, desc: "Flat or blown tire swapped for your spare, roadside." },
  { name: "Fuel Delivery", icon: Fuel, desc: "Ran empty? We bring fuel to you, wherever you're stopped." },
  { name: "Winch-Out / Recovery", icon: Anchor, desc: "Stuck in mud, sand, or a ditch? We pull you out." },
  { name: "Vehicle Transportation", icon: CarFront, desc: "Scheduled transport for cars, trucks, and SUVs." },
  { name: "Medium-Duty Recovery", icon: Construction, desc: "Box trucks, work trucks, and medium-duty vehicles handled right." },
  { name: "Commercial & Fleet Support", icon: Building2, desc: "Towing, transport, and roadside support for businesses and fleets." },
];

const CITIES = [
  "Fort Lauderdale", "Wilton Manors", "Oakland Park", "Pompano Beach",
  "Deerfield Beach", "Lauderdale Lakes", "Plantation", "Davie",
];

const scrollToForm = () => {
  document.getElementById("request-service")?.scrollIntoView({ behavior: "smooth" });
};

const ActionButtons = ({ compact = false }) => (
  <div className={`flex ${compact ? "flex-row" : "flex-col sm:flex-row"} gap-3 w-full sm:w-auto`}>
    <a href={PHONE_TEL} data-testid="call-now-btn"
      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#E52E2E] hover:bg-[#C82323] text-white font-display font-bold uppercase tracking-wider text-lg px-7 py-3.5 transition-colors">
      <Phone className="w-5 h-5" /> Call Now
    </a>
    <a href={PHONE_SMS} data-testid="text-now-btn"
      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#FFFDD0] hover:bg-white text-[#0D0E11] font-display font-bold uppercase tracking-wider text-lg px-7 py-3.5 transition-colors">
      <MessageSquare className="w-5 h-5" /> Text Now
    </a>
    <button onClick={scrollToForm} data-testid="request-service-btn"
      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border-2 border-white/70 hover:border-[#E52E2E] hover:text-[#E52E2E] text-white font-display font-bold uppercase tracking-wider text-lg px-7 py-3.5 transition-colors">
      <ClipboardList className="w-5 h-5" /> Request Service
    </button>
  </div>
);

const Header = () => (
  <header data-testid="site-header" className="sticky top-0 z-40 bg-[#0D0E11]/95 backdrop-blur border-b border-[#2A2E37]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-3">
      <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
        <img src={LOGO} alt="Bachan's Towing & Transportation 25HR logo" className="h-14 sm:h-[72px] w-auto mix-blend-screen" />
      </a>
      <nav className="hidden lg:flex items-center gap-7 font-display uppercase tracking-wider text-sm text-[#F3EFE0]">
        <a data-testid="nav-services" href="#services" className="hover:text-[#E52E2E] transition-colors">Services</a>
        <a data-testid="nav-about" href="#trust" className="hover:text-[#E52E2E] transition-colors">About</a>
        <a data-testid="nav-commercial" href="#commercial" className="hover:text-[#E52E2E] transition-colors">Commercial</a>
        <a data-testid="nav-areas" href="#service-area" className="hover:text-[#E52E2E] transition-colors">Service Area</a>
        <a data-testid="nav-reviews" href="#reviews" className="hover:text-[#E52E2E] transition-colors">Reviews</a>
      </nav>
      <a href={PHONE_TEL} data-testid="header-call-btn"
        className="inline-flex items-center gap-2 bg-[#E52E2E] hover:bg-[#C82323] text-white font-display font-bold uppercase tracking-wider text-base sm:text-lg px-4 sm:px-6 py-2.5 transition-colors">
        <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
        <span>{PHONE_DISPLAY}</span>
      </a>
    </div>
  </header>
);

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative">
    <div className="hero-bg absolute inset-0" style={{ backgroundImage: `url(${IMG_LOT})` }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0D0E11]/80 via-[#0D0E11]/70 to-[#0D0E11]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
      <div className="inline-flex items-center gap-2 border border-[#E52E2E] bg-[#0D0E11]/70 px-4 py-1.5 mb-6" data-testid="hero-25hr-badge">
        <Clock className="w-4 h-4 text-[#E52E2E]" />
        <span className="font-display uppercase tracking-widest text-sm text-[#FFFDD0]">25HR — Always On Call</span>
      </div>
      <h1 data-testid="hero-headline" className="font-display font-black uppercase leading-[0.95] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
        When the road stops.<br /><span className="text-[#E52E2E]">We don't.</span>
      </h1>
      <p className="mt-5 max-w-xl text-[#F3EFE0] text-base sm:text-lg">
        Owner-operated towing, recovery, and vehicle transportation serving Greater Fort Lauderdale &amp; Broward County.
        You call — you talk directly to the person behind the wheel.
      </p>
      <div className="mt-8">
        <ActionButtons />
      </div>
      <a href={PHONE_TEL} data-testid="hero-phone-link" className="mt-6 inline-block font-display text-2xl sm:text-3xl font-bold text-[#FFFDD0] hover:text-[#E52E2E] transition-colors tracking-wide">
        {PHONE_DISPLAY}
      </a>
    </div>
  </section>
);

const AvailabilityStrip = () => (
  <div data-testid="availability-strip" className="bg-[#E52E2E] border-y-4 border-[#0D0E11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-8 text-center">
      <span className="font-display font-black uppercase tracking-widest text-white text-xl sm:text-2xl">25-Hour Availability</span>
      <span className="hidden sm:inline text-white/60 font-display text-2xl">/</span>
      <span className="font-display font-bold uppercase tracking-widest text-[#0D0E11] text-lg sm:text-xl">Nights · Weekends · Holidays</span>
    </div>
  </div>
);

const Services = () => (
  <section id="services" data-testid="services-section" className="py-16 sm:py-24 bg-[#0D0E11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
        <div>
          <div className="red-rule mb-4" />
          <p className="font-display uppercase tracking-widest text-[#E52E2E] text-sm font-semibold">What we do</p>
          <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight">Towing &amp; Roadside Services</h2>
        </div>
        <button onClick={scrollToForm} data-testid="services-request-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#E52E2E] hover:bg-[#C82323] text-white font-display font-bold uppercase tracking-wider text-lg px-7 py-3 transition-colors shrink-0">
          <ClipboardList className="w-5 h-5" /> Request Service
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {SERVICES.map(({ name, icon: Icon, desc }) => (
          <div key={name} data-testid={`service-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group bg-[#1C2026] border border-[#2A2E37] hover:border-[#E52E2E] border-t-4 border-t-[#E52E2E] p-6 transition-colors">
            <Icon className="w-8 h-8 text-[#E52E2E] mb-4" />
            <h3 className="font-display font-bold uppercase text-white text-xl tracking-wide">{name}</h3>
            <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{desc}</p>
          </div>
        ))}
        <button onClick={scrollToForm} data-testid="services-grid-cta-card"
          className="group bg-[#E52E2E] hover:bg-[#C82323] p-6 transition-colors text-left flex flex-col justify-between min-h-[160px]">
          <ClipboardList className="w-8 h-8 text-white mb-4" />
          <span className="font-display font-black uppercase text-white text-2xl tracking-wide leading-tight">
            Need help now?<br />Request service →
          </span>
        </button>
      </div>
    </div>
  </section>
);

const TRUST_POINTS = [
  { icon: User, title: "Owner-Operated", desc: "You deal directly with the owner — the same person who shows up to your vehicle." },
  { icon: Phone, title: "Direct Communication", desc: "No call centers, no runaround. Call or text and get a straight answer, fast." },
  { icon: ShieldCheck, title: "Professional Handling", desc: "Your vehicle is hooked, hauled, and delivered with professional care." },
  { icon: Building2, title: "Individuals & Businesses", desc: "From a stranded commuter to a full commercial fleet — one number handles it." },
  { icon: MapPin, title: "Local Towing & Recovery", desc: "Based in and built around Greater Fort Lauderdale / Broward County." },
  { icon: Truck, title: "Real Equipment, Real Work", desc: "Every photo on this site is our actual truck on actual jobs." },
];

const Trust = () => (
  <section id="trust" data-testid="trust-section" className="py-16 sm:py-24 bg-[#15181E]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="red-rule mb-4" />
      <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight max-w-3xl">
        Real equipment. Real experience. <span className="text-[#E52E2E]">Real help.</span>
      </h2>
      <p className="mt-4 text-[#9CA3AF] max-w-2xl text-base sm:text-lg">
        Bachan's Towing &amp; Transportation is a locally owned, owner-operated company. When you call, you get the person who does the work.
      </p>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TRUST_POINTS.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="bg-[#0D0E11] border border-[#2A2E37] p-6">
            <Icon className="w-7 h-7 text-[#E52E2E] mb-3" />
            <h3 className="font-display font-bold uppercase text-[#FFFDD0] text-lg tracking-wide">{title}</h3>
            <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GALLERY = [
  { src: IMG_LOT, caption: "Dollies-out tow — SUV transport in Broward County" },
  { src: IMG_STREET, caption: "Medium-duty work truck recovery" },
  { src: IMG_GARAGE, caption: "Low-clearance garage extraction — handled" },
];

const Gallery = () => (
  <section data-testid="gallery-section" className="py-16 sm:py-24 bg-[#0D0E11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="red-rule mb-4" />
      <p className="font-display uppercase tracking-widest text-[#E52E2E] text-sm font-semibold">Proof, not promises</p>
      <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight">The Real Truck. The Real Work.</h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {GALLERY.map(({ src, caption }, i) => (
          <figure key={caption} data-testid={`gallery-photo-${i + 1}`} className="group border border-[#2A2E37] bg-[#15181E]">
            <div className="overflow-hidden aspect-[4/3]">
              <img src={src} alt={caption} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            </div>
            <figcaption className="px-4 py-3 border-t-2 border-[#E52E2E] font-display uppercase tracking-wide text-sm text-[#FFFDD0]">
              {caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const Commercial = ({ onRequestCommercial }) => (
  <section id="commercial" data-testid="commercial-section" className="py-16 sm:py-20 bg-[#E52E2E] relative overflow-hidden">
    <div className="hazard-stripes absolute top-0 left-0 right-0 h-2 opacity-60" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
      <div className="flex-1">
        <p className="font-display uppercase tracking-widest text-[#0D0E11] text-sm font-bold">For businesses</p>
        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight mt-2">
          Commercial &amp; Fleet Support
        </h2>
        <p className="mt-4 text-white/90 max-w-xl text-base sm:text-lg">
          Towing, vehicle transportation, roadside assistance, and recovery for businesses and fleets —
          repair shops, dealerships, property managers, and work-truck fleets across Broward County.
          One direct line to the owner.
        </p>
      </div>
      <div className="flex flex-col gap-3 shrink-0">
        <button onClick={onRequestCommercial} data-testid="request-commercial-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#0D0E11] hover:bg-[#1C2026] text-white font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
          <Building2 className="w-5 h-5" /> Request Commercial Service
        </button>
        <a href={PHONE_TEL} data-testid="commercial-call-btn"
          className="inline-flex items-center justify-center gap-2 border-2 border-[#0D0E11] text-[#0D0E11] hover:bg-[#0D0E11] hover:text-white font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
          <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
        </a>
      </div>
    </div>
    <div className="hazard-stripes absolute bottom-0 left-0 right-0 h-2 opacity-60" />
  </section>
);

const ServiceArea = () => (
  <section id="service-area" data-testid="service-area-section" className="py-16 sm:py-24 bg-[#15181E]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="red-rule mb-4" />
      <p className="font-display uppercase tracking-widest text-[#E52E2E] text-sm font-semibold">Where we work</p>
      <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight">
        Greater Fort Lauderdale / Broward County
      </h2>
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {CITIES.map((city) => (
          <div key={city} data-testid={`area-${city.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-center gap-2.5 bg-[#0D0E11] border border-[#2A2E37] px-4 py-3.5">
            <MapPin className="w-4 h-4 text-[#E52E2E] shrink-0" />
            <span className="font-display uppercase tracking-wide text-[#FFFDD0] text-base sm:text-lg">{city}</span>
          </div>
        ))}
      </div>
      <p className="mt-6 text-[#9CA3AF] text-sm sm:text-base">
        Surrounding Broward County area too — call <a href={PHONE_TEL} data-testid="area-phone-link" className="text-[#E52E2E] font-semibold hover:underline">{PHONE_DISPLAY}</a> and ask.
      </p>
    </div>
  </section>
);

const Reviews = () => (
  <section id="reviews" data-testid="reviews-section" className="py-16 sm:py-24 bg-[#0D0E11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="red-rule mb-4" />
      <p className="font-display uppercase tracking-widest text-[#E52E2E] text-sm font-semibold">Customer feedback</p>
      <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight">Google Reviews</h2>
      <div className="mt-10 bg-[#1C2026] border border-[#2A2E37] border-t-4 border-t-[#E52E2E] p-8 sm:p-12 text-center max-w-3xl" data-testid="reviews-placeholder">
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-6 h-6 text-[#2A2E37]" fill="currentColor" />
          ))}
        </div>
        <p className="font-display font-bold uppercase text-[#FFFDD0] text-xl sm:text-2xl tracking-wide">
          Real reviews from real customers — coming soon
        </p>
        <p className="mt-3 text-[#9CA3AF] text-sm sm:text-base max-w-md mx-auto">
          Our live Google Business Profile reviews will be displayed here. We don't post fake reviews — only feedback from customers we've actually helped.
        </p>
        <p className="mt-5 text-sm text-[#6B7280]">
          Had a good experience? Text us at <a href={PHONE_SMS} data-testid="reviews-text-link" className="text-[#E52E2E] font-semibold hover:underline">{PHONE_DISPLAY}</a> and let us know.
        </p>
      </div>
    </div>
  </section>
);

const InstagramSection = () => (
  <section data-testid="instagram-section" className="py-14 sm:py-20 bg-[#15181E] border-y border-[#2A2E37]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-[#E52E2E] flex items-center justify-center shrink-0">
          <Instagram className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="font-display font-black uppercase text-white text-2xl sm:text-3xl tracking-tight">Follow the work</h2>
          <p className="text-[#9CA3AF] text-sm sm:text-base">Recent jobs, the truck, and the daily grind.</p>
        </div>
      </div>
      <a href="https://www.instagram.com/bachanstowing" target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-btn"
        className="inline-flex items-center gap-2 border-2 border-[#E52E2E] text-[#E52E2E] hover:bg-[#E52E2E] hover:text-white font-display font-bold uppercase tracking-wider text-lg px-8 py-3.5 transition-colors">
        <Instagram className="w-5 h-5" /> @BACHANSTOWING
      </a>
    </div>
  </section>
);

const CONDITION_OPTIONS = ["Runs & Drives", "Won't Start", "Accident Damage", "Stuck / Needs Winch-Out", "Flat Tire(s)", "Other"];
const inputCls = "w-full bg-[#0D0E11] border border-[#2A2E37] focus:border-[#E52E2E] outline-none text-white placeholder-[#6B7280] px-4 py-3 text-base transition-colors";
const labelCls = "block font-display uppercase tracking-wider text-[#FFFDD0] text-sm font-semibold mb-1.5";

const RequestForm = ({ service, setService }) => {
  const [form, setForm] = useState({ name: "", phone: "", pickup: "", destination: "", vehicle: "", condition: "", details: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number so we can reach you.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("service", service);
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/service-requests`, fd);
      toast.success("Request received. We'll call or text you back shortly.");
      setForm({ name: "", phone: "", pickup: "", destination: "", vehicle: "", condition: "", details: "" });
      setPhoto(null);
      setService("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="request-service" data-testid="request-form-section" className="py-16 sm:py-24 bg-[#0D0E11]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="red-rule mb-4" />
        <p className="font-display uppercase tracking-widest text-[#E52E2E] text-sm font-semibold">Fast response</p>
        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl tracking-tight">Request Service</h2>
        <p className="mt-3 text-[#9CA3AF] text-sm sm:text-base">
          Send the details and we'll respond quickly to confirm availability and pricing. In a hurry?{" "}
          <a href={PHONE_TEL} data-testid="form-call-link" className="text-[#E52E2E] font-semibold hover:underline">Call {PHONE_DISPLAY}</a>.
        </p>
        <form onSubmit={submit} data-testid="request-service-form" className="mt-8 bg-[#15181E] border border-[#2A2E37] border-t-4 border-t-[#E52E2E] p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls} htmlFor="req-name">Name *</label>
              <input id="req-name" data-testid="form-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" required />
            </div>
            <div>
              <label className={labelCls} htmlFor="req-phone">Phone *</label>
              <input id="req-phone" data-testid="form-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls} htmlFor="req-pickup">Pickup Location</label>
              <input id="req-pickup" data-testid="form-pickup-input" className={inputCls} value={form.pickup} onChange={set("pickup")} placeholder="Street, cross-street, or address" />
            </div>
            <div>
              <label className={labelCls} htmlFor="req-destination">Destination</label>
              <input id="req-destination" data-testid="form-destination-input" className={inputCls} value={form.destination} onChange={set("destination")} placeholder="Where's it going?" />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="req-vehicle">Vehicle Year / Make / Model</label>
            <input id="req-vehicle" data-testid="form-vehicle-input" className={inputCls} value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2019 Honda Accord" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls} htmlFor="req-service">Service Needed</label>
              <select id="req-service" data-testid="form-service-select" className={inputCls} value={service} onChange={(e) => setService(e.target.value)}>
                <option value="">Select a service…</option>
                {SERVICES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                <option value="Not Sure / Other">Not Sure / Other</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="req-condition">Vehicle Condition</label>
              <select id="req-condition" data-testid="form-condition-select" className={inputCls} value={form.condition} onChange={set("condition")}>
                <option value="">Select condition…</option>
                {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="req-details">Additional Details</label>
            <textarea id="req-details" data-testid="form-details-textarea" rows={4} className={inputCls} value={form.details} onChange={set("details")} placeholder="Anything else we should know — gate codes, exact spot, situation…" />
          </div>
          <div>
            <label className={labelCls} htmlFor="req-photo">Photo Upload <span className="text-[#6B7280] normal-case tracking-normal">(optional)</span></label>
            <div className="flex items-center gap-3 flex-wrap">
              <label data-testid="form-photo-label" className="inline-flex items-center gap-2 border border-dashed border-[#2A2E37] hover:border-[#E52E2E] text-[#9CA3AF] hover:text-white px-5 py-3 cursor-pointer transition-colors">
                <ImagePlus className="w-5 h-5" />
                <span className="text-sm">{photo ? photo.name : "Add a photo of the vehicle or situation"}</span>
                <input id="req-photo" ref={fileRef} data-testid="form-photo-input" type="file" accept="image/*" className="hidden"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
              </label>
              {photo && (
                <button type="button" data-testid="form-photo-remove" onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="inline-flex items-center gap-1 text-[#E52E2E] text-sm hover:underline">
                  <X className="w-4 h-4" /> Remove
                </button>
              )}
            </div>
          </div>
          <button type="submit" disabled={sending} data-testid="form-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E52E2E] hover:bg-[#C82323] disabled:opacity-60 text-white font-display font-black uppercase tracking-widest text-xl px-8 py-4 transition-colors">
            <Send className="w-5 h-5" /> {sending ? "Sending…" : "Send Request"}
          </button>
          <p className="text-xs text-[#6B7280] leading-relaxed" data-testid="form-disclaimer">
            Submitting this form sends your details directly to Bachan's Towing &amp; Transportation so we can respond quickly.
            It does not confirm dispatch or set a final price — we'll confirm everything with you by phone or text first.
          </p>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0D0E11] border-t border-[#2A2E37] pt-14 pb-28 md:pb-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
      <div>
        <img src={LOGO} alt="Bachan's Towing & Transportation 25HR logo" className="h-24 w-auto mb-4 mix-blend-screen" />
        <p className="font-display font-bold uppercase text-white text-xl tracking-wide">Bachan's Towing &amp; Transportation</p>
        <p className="text-[#9CA3AF] text-sm mt-1">Greater Fort Lauderdale / Broward County, FL</p>
      </div>
      <div>
        <h3 className="font-display font-bold uppercase text-[#FFFDD0] tracking-widest text-sm mb-4">Contact</h3>
        <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-2 text-[#E52E2E] font-display font-bold text-xl hover:underline">
          <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
        </a>
        <a href={PHONE_SMS} data-testid="footer-text-link" className="flex items-center gap-2 text-[#9CA3AF] text-sm mt-2 hover:text-white transition-colors">
          <MessageSquare className="w-4 h-4" /> Text us anytime — 25HR
        </a>
        <a href="https://www.instagram.com/bachanstowing" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link"
          className="flex items-center gap-2 text-[#9CA3AF] text-sm mt-2 hover:text-white transition-colors">
          <Instagram className="w-4 h-4" /> @BACHANSTOWING
        </a>
      </div>
      <div>
        <h3 className="font-display font-bold uppercase text-[#FFFDD0] tracking-widest text-sm mb-4">Services</h3>
        <ul className="space-y-1.5 text-sm text-[#9CA3AF]">
          {SERVICES.slice(0, 6).map((s) => (
            <li key={s.name}><a href="#services" className="hover:text-[#E52E2E] transition-colors">{s.name}</a></li>
          ))}
          <li><a href="#services" data-testid="footer-all-services-link" className="text-[#E52E2E] hover:underline">View all services →</a></li>
        </ul>
      </div>
      <div>
        <h3 className="font-display font-bold uppercase text-[#FFFDD0] tracking-widest text-sm mb-4">Get Help</h3>
        <button onClick={scrollToForm} data-testid="footer-request-btn"
          className="inline-flex items-center gap-2 bg-[#E52E2E] hover:bg-[#C82323] text-white font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors">
          <ClipboardList className="w-4 h-4" /> Request Service
        </button>
        <p className="text-[#6B7280] text-xs mt-4 leading-relaxed">
          Towing · Roadside Assistance · Vehicle Transport · Medium-Duty Recovery — Fort Lauderdale &amp; Broward County.
        </p>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-12 pt-6 border-t border-[#2A2E37] flex flex-col sm:flex-row items-center justify-between gap-2">
      <p className="text-[#6B7280] text-xs">© {new Date().getFullYear()} Bachan's Towing &amp; Transportation. All rights reserved.</p>
      <p className="font-display uppercase tracking-widest text-[#E52E2E] text-xs">25HR — Always On Call</p>
    </div>
  </footer>
);

const StickyMobileBar = () => (
  <div data-testid="sticky-mobile-bar" className="md:hidden fixed bottom-0 left-0 right-0 z-50 grid grid-cols-3 border-t-2 border-[#E52E2E] bg-[#0D0E11]">
    <a href={PHONE_TEL} data-testid="sticky-call-btn"
      className="flex flex-col items-center justify-center gap-1 py-3 bg-[#E52E2E] text-white active:bg-[#C82323]">
      <Phone className="w-5 h-5" />
      <span className="font-display font-bold uppercase tracking-wider text-sm">Call</span>
    </a>
    <a href={PHONE_SMS} data-testid="sticky-text-btn"
      className="flex flex-col items-center justify-center gap-1 py-3 bg-[#FFFDD0] text-[#0D0E11] active:bg-white border-x border-[#0D0E11]">
      <MessageSquare className="w-5 h-5" />
      <span className="font-display font-bold uppercase tracking-wider text-sm">Text</span>
    </a>
    <button onClick={scrollToForm} data-testid="sticky-request-btn"
      className="flex flex-col items-center justify-center gap-1 py-3 bg-[#0D0E11] text-white active:bg-[#1C2026]">
      <ClipboardList className="w-5 h-5 text-[#E52E2E]" />
      <span className="font-display font-bold uppercase tracking-wider text-sm">Request</span>
    </button>
  </div>
);

export default function App() {
  const [service, setService] = useState("");

  const requestCommercial = () => {
    setService("Commercial & Fleet Support");
    scrollToForm();
    toast.info("Commercial request — add your details and we'll follow up.");
  };

  return (
    <div className="App">
      <Toaster position="top-center" theme="dark" />
      <Header />
      <main>
        <Hero />
        <AvailabilityStrip />
        <Services />
        <Trust />
        <Gallery />
        <Commercial onRequestCommercial={requestCommercial} />
        <ServiceArea />
        <Reviews />
        <InstagramSection />
        <RequestForm service={service} setService={setService} />
      </main>
      <Footer />
      <StickyMobileBar />
    </div>
  );
}
